
import { Component } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { Online } from '@/components/mixins/online';
import { getClubUsers, retrieveJsonKey } from '@/services/auth';
import { AuthUser } from '@/models/authUser';
import NumericKeypad from '@/components/coach/NumericKeypad.vue';
import moment from 'moment';

@Component({
  components: {
    NumericKeypad,
  },
})
export default class SwitchUser extends mixins(Online) {
  private showKeypad = false;
  private selectedUser = {} as AuthUser;
  private status = '';
  private origUser = {} as AuthUser;
  private loading = false;
  private allGroupsImported = false;

  async mounted() {
    localStorage.setItem('switching-user', '1');
    this.allGroupsImported = (await this.$store.dispatch('storeDb/getSetting', 'allGroupsImported')) || false;
    // Assume if groups doesn't have more than 1 teacher then all groups haven't yet been imported
    if (this.allGroupsImported) {
      return;
    }

    if (!this.online) {
      this.$alert('error', this.$t('switch_users.not_online'));
      this.$router.push({ name: 'member-groups', query: { switch: '1' } });
      return;
    }

    // If session date not today then we need to reset it and refetch all groups
    const today = moment().format('YYYY-MM-DD');
    if (this.$store.getters['groups/sessionDate'] !== today) {
      await this.$store.dispatch('groups/updateDate', today);
      await this.$store.dispatch('storeDb/setSetting', { setting: 'allGroupsImported', value: false });
    }

    if (this.$store.getters['groups/canImportAllGroups']()) {
      await this.importAllGroups();
    } else {
      this.$alert('error', this.$t('switch_users.cannot_import_groups'));
    }
  }

  get clubUsers() {
    const origUserUuid = localStorage.getItem('origUserUuid');
    const users = getClubUsers();
    const origUser = users.find((u: AuthUser) => u.uuid === origUserUuid);
    if (users && origUser) {
      origUser.loggedIn = true;
      this.origUser = origUser;
    }
    let teacherNgoIds = this.$store.state.groups.teacherNgoIds;
    if (localStorage.getItem('switching-user')) {
      teacherNgoIds = retrieveJsonKey('teacherNgoIds') || [];
    }
    return users.filter((u: AuthUser) => u.uuid === origUserUuid || teacherNgoIds.includes(u.ngo_id));
  }

  switchUser(user: AuthUser) {
    this.status = '';
    this.selectedUser = user;
    this.showKeypad = true;
  }

  async importAllGroups() {
    const n = await this.$store.dispatch('groups/fetchItems', { forceSync: true, allGroups: true });
    this.$alert('success', this.$t('switch_users.download_success', { n }));
    this.allGroupsImported = (await this.$store.dispatch('storeDb/getSetting', 'allGroupsImported')) || false;
  }

  async checkPinCode(enteredCode: string) {
    const code = parseInt(enteredCode, 10);
    // Allow user logged in via username/password to switch to any teacher with THEIR pin code
    // The original user's pin ALWAYS works.
    const selectedUserCode = this.selectedUser.pin_code ? parseInt(this.selectedUser.pin_code, 10) : false;
    const origUserCode = this.origUser.pin_code ? parseInt(this.origUser.pin_code, 10) : false;
    if (code === selectedUserCode || code === origUserCode) {
      this.loading = true;
      await this.$store.dispatch('auth/switchUser', this.selectedUser.uuid);
      // reset to today's date and load groups
      await this.$store.dispatch('groups/changeDate', { date: moment().format('YYYY-MM-DD'), force: true });
      this.loading = false;
      localStorage.removeItem('switching-user');
      this.$router.push({ path: '/', query: { switch: '1' } });
      this.$alert('success', this.$t('switch_users.switched_user'));
    } else {
      this.status = 'error';
    }
  }
}
